import { useState, useRef, useCallback } from 'react';
import { tbl } from '../../theme';

const STATUS_COLOR = {
  good: { dot: '#16A34A', var: '#16A34A', rowBg: '' },
  risk: { dot: '#CA8A04', var: '#A16207', rowBg: '' },
  off:  { dot: '#DC2626', var: '#DC2626', rowBg: '#FFF8F8' },
  none: { dot: '#D1D5DB', var: '#9CA3AF', rowBg: '' },
};

function varStr(v) {
  if (v === null || v === undefined) return '—';
  return (v > 0 ? '▲ +' : '▼ ') + Math.abs(v).toFixed(1) + '%';
}

export default function MetricTable({ metrics, isLocked, canComment, onCommentChange }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const debounceRef         = useRef({});

  const filtered = metrics.filter((m) => {
    if (filter !== 'all' && m.status !== filter) return false;
    if (search && !m.metricName.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const handleComment = useCallback((metricValueId, value) => {
    clearTimeout(debounceRef.current[metricValueId]);
    debounceRef.current[metricValueId] = setTimeout(() => {
      onCommentChange(metricValueId, value);
    }, 500);
  }, [onCommentChange]);

  return (
    <div style={s.wrap}>
      <div style={s.filterBar}>
        {['all', 'good', 'risk', 'off'].map((f) => (
          <button
            key={f}
            style={{ ...s.flt, ...(filter === f ? s.fltActive : {}) }}
            onClick={() => setFilter(f)}
          >
            {f !== 'all' && (
              <span style={{ ...s.fltDot, background: STATUS_COLOR[f]?.dot }} />
            )}
            {f === 'all' ? 'All' : f === 'good' ? 'On Target' : f === 'risk' ? 'At Risk' : 'Off Track'}
          </button>
        ))}
        <input
          style={s.search}
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <colgroup>
            <col style={{ width: '30%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '9%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '32%' }} />
          </colgroup>
          <thead>
            <tr>
              {['Metric', 'Actual', 'Budget', 'YTD', 'Var %', 'Leadership Comment'].map((h, i) => (
                <th key={h} style={{ ...s.th, textAlign: i > 0 && i < 5 ? 'right' : 'left' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} style={s.emptyCell}>No metrics match this filter.</td>
              </tr>
            )}
            {filtered.map((m) => (
              <MetricRow
                key={m.id}
                metric={m}
                isLocked={isLocked}
                canComment={canComment}
                onComment={handleComment}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function MetricRow({ metric: m, isLocked, canComment, onComment }) {
  const [localComment, setLocalComment] = useState(m.userComment || '');
  const textareaRef = useRef(null);
  const colors      = STATUS_COLOR[m.status] || STATUS_COLOR.none;

  function handleChange(e) {
    const val = e.target.value;
    setLocalComment(val);
    e.target.style.height = 'auto';
    e.target.style.height = `${e.target.scrollHeight}px`;
    onComment(m.id, val);
  }

  const hasComment = localComment.trim().length > 0;
  const hasOtherComments = m.allComments && m.allComments.length > 0;

  const rowStyle = {
    ...s.tr,
    background: hasComment || hasOtherComments ? '#F0FDF4' : (colors.rowBg || '#fff'),
  };

  return (
    <tr style={rowStyle}>
      <td style={s.tdLeft}>
        <div style={s.metricCell}>
          <span style={{ ...s.dot, background: colors.dot }} />
          <span style={s.metricName}>{m.metricName}</span>
        </div>
      </td>
      <td style={s.tdRight}>{m.actualDisplay || '—'}</td>
      <td style={s.tdRight}>{m.budgetDisplay || '—'}</td>
      <td style={s.tdRight}>{m.ytdDisplay || '—'}</td>
      <td style={{ ...s.tdRight, color: colors.var, fontWeight: m.status !== 'none' ? 600 : 400 }}>
        {varStr(m.variancePct)}
      </td>
      <td style={s.tdLeft}>
        {/* All comments from all users */}
        {hasOtherComments && (
          <div style={s.allComments}>
            {m.allComments.map((c, i) => (
              <div key={i} style={s.commentItem}>
                <span style={s.commentAuthor}>{c.user}:</span>
                <span style={s.commentText}> {c.comment}</span>
              </div>
            ))}
          </div>
        )}
        {/* Editable field for current user — hidden when review is submitted
            or the user does not have can_comment for this cost center */}
        {!isLocked && canComment && (
          <textarea
            ref={textareaRef}
            style={{
              ...s.textarea,
              borderColor: hasComment ? '#86EFAC' : '#E5E7EB',
              background:  hasComment ? '#fff'    : '#F9FAFB',
              marginTop:   hasOtherComments ? '6px' : '0',
            }}
            rows={1}
            value={localComment}
            onChange={handleChange}
            placeholder="Add your comment…"
          />
        )}
        {(isLocked || !canComment) && !hasOtherComments && (
          <em style={{ color: '#D1D5DB', fontSize: '11px' }}>No comment</em>
        )}
      </td>
    </tr>
  );
}

const s = {
  wrap:          { border: '1px solid #E5E7EB', borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden', marginBottom: '2px' },
  filterBar:     { display: 'flex', gap: '6px', padding: '8px 12px', background: '#F9FAFB', borderBottom: '1px solid #E5E7EB', flexWrap: 'wrap', alignItems: 'center' },
  flt:           { padding: '4px 10px', fontSize: '11px', border: '1px solid #E5E7EB', borderRadius: '20px', background: '#fff', color: '#6B7280', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', fontFamily: 'inherit' },
  fltActive:     { background: '#EFF6FF', borderColor: '#93C5FD', color: '#1D4ED8', fontWeight: 500 },
  fltDot:        { width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0 },
  search:        { marginLeft: 'auto', padding: '4px 10px', fontSize: '11px', border: '1px solid #E5E7EB', borderRadius: '20px', background: '#fff', color: '#111827', fontFamily: 'inherit', width: '140px' },
  tableWrap:     { overflowX: 'auto' },
  table:         { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' },
  th:            { ...tbl.th, ...tbl.thSm, fontSize: '9px', letterSpacing: '0.8px' },
  tr:            { ...tbl.tr, ...tbl.tr, transition: 'background 0.1s' },
  tdLeft:        { padding: '8px 10px', verticalAlign: 'top', fontSize: '12px' },
  tdRight:       { padding: '8px 10px', textAlign: 'right', verticalAlign: 'middle', fontSize: '12px', color: '#374151' },
  metricCell:    { display: 'flex', alignItems: 'center', gap: '8px' },
  dot:           { width: '7px', height: '7px', borderRadius: '50%', flexShrink: 0 },
  metricName:    { color: '#374151', fontSize: '12px' },
  textarea:      { width: '100%', border: '1px solid #E5E7EB', borderRadius: '5px', padding: '5px 8px', fontSize: '11px', color: '#374151', fontFamily: 'inherit', resize: 'none', lineHeight: 1.5, overflow: 'hidden', minHeight: '28px', transition: 'border-color 0.15s, background 0.15s' },
  emptyCell:     { textAlign: 'center', padding: '24px', fontSize: '12px', color: '#9CA3AF' },
  allComments:   { display: 'flex', flexDirection: 'column', gap: '4px' },
  commentItem:   { fontSize: '11px', lineHeight: 1.5, background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: '4px', padding: '4px 7px' },
  commentAuthor: { fontWeight: 600, color: '#166534' },
  commentText:   { color: '#374151' },
};